import React from 'react';
import { colors, spacing, typography } from '../../styles/designTokens';
import Button from './Button';

/**
 * EmptyState Component
 * 
 * User-friendly empty state with helpful guidance and clear actions.
 * Replaces plain "No data" messages throughout the application.
 * 
 * Usage:
 * <EmptyState
 *   icon="📞"
 *   title="No calls yet"
 *   description="Make your first call to start tracking leads!"
 *   action={<Button onClick={goToCallScript}>Start Your First Call</Button>}
 * />
 * 
 * Props:
 * - icon: String or React node - illustrative icon/emoji
 * - title: String - main message (required)
 * - description: String - helpful description
 * - action: React node - primary call-to-action button
 * - secondaryAction: React node - optional secondary action
 * - className: Additional CSS classes
 */

const EmptyState = ({
  icon,
  title,
  description,
  action,
  secondaryAction,
  className = ''
}) => {
  return (
    <div
      className={`flex flex-col items-center justify-center text-center ${className}`}
      style={{
        padding: `${spacing['2xl']} ${spacing.xl}`,
        minHeight: '300px'
      }}
      role="status"
      aria-live="polite"
    >
      {/* Icon */}
      {icon && (
        <div
          style={{
            fontSize: '4rem',
            marginBottom: spacing.lg,
            opacity: 0.8
          }}
          aria-hidden="true"
        >
          {typeof icon === 'string' ? icon : icon}
        </div>
      )}

      {/* Title */}
      <h3
        style={{
          fontSize: typography.h2.size,
          fontWeight: typography.h2.weight,
          color: colors.neutral[900],
          marginBottom: spacing.sm,
          margin: 0
        }}
      >
        {title}
      </h3>

      {/* Description */}
      {description && (
        <p
          style={{
            fontSize: typography.body.size,
            color: colors.neutral[600],
            marginBottom: spacing.lg,
            maxWidth: '500px',
            lineHeight: 1.6
          }}
        >
          {description}
        </p>
      )}

      {/* Actions */}
      {(action || secondaryAction) && (
        <div
          style={{
            display: 'flex',
            gap: spacing.md,
            marginTop: spacing.md,
            flexWrap: 'wrap',
            justifyContent: 'center'
          }}
        >
          {action}
          {secondaryAction}
        </div>
      )}
    </div>
  );
};

/**
 * EmptyStateList Component
 * 
 * Specialized empty state for lists and tables
 * 
 * Usage:
 * <EmptyStateList
 *   searchQuery={searchTerm}
 *   resourceName="users"
 *   onClearFilters={clearFilters}
 * />
 */
export const EmptyStateList = ({
  searchQuery,
  resourceName = 'items',
  onClearFilters,
  className = ''
}) => {
  const hasFilters = !!searchQuery || !!onClearFilters;

  return (
    <EmptyState
      icon="🔍"
      title={hasFilters ? `No ${resourceName} found` : `No ${resourceName} yet`}
      description={
        hasFilters
          ? `No ${resourceName} match your current filters. Try adjusting your search criteria.`
          : `You don't have any ${resourceName} yet. They'll appear here once created.`
      }
      action={
        hasFilters && onClearFilters ? (
          <Button variant="outline" onClick={onClearFilters}>
            Clear Filters
          </Button>
        ) : null
      }
      className={className}
    />
  );
};

/**
 * EmptyStateError Component
 * 
 * Empty state for error conditions
 * 
 * Usage:
 * <EmptyStateError
 *   title="Failed to load data"
 *   description="We couldn't load your leads. Please try again."
 *   onRetry={handleRetry}
 * />
 */
export const EmptyStateError = ({
  title = 'Something went wrong',
  description,
  onRetry,
  className = ''
}) => {
  return (
    <EmptyState
      icon="⚠️"
      title={title}
      description={description}
      action={
        onRetry ? (
          <Button variant="primary" onClick={onRetry}>
            Try Again
          </Button>
        ) : null
      }
      className={className}
    />
  );
};

export default EmptyState;

